import { useAuth } from "@/auth/auth";
import type { components } from "@/client/backend/zz_schema";
import { useMemo } from "react";
import useSWR, { mutate } from "swr";

// Scope handling mirrors pkg/authn/scope.go: a stored scope is
// `<resource>:<id|*>:<action>`, wildcards live only on the stored side, and
// `all` covers every action. The shared fixture at
// pkg/authn/testdata/scope_cases.json pins both implementations.

export type KeyScopeBadge = { label: "Read-only" } | { label: "Custom"; scopes: string[] };

/**
 * What to show beside an API key. Nothing for full access, a badge when every
 * scope only reads, the raw scopes for anything else so a key minted with a
 * narrower set is visible for what it is. A member's default key is spelled
 * out rather than a wildcard, so it shows its scopes too; that is truthful.
 */
export function keyScopeBadge(scopes: string[] | undefined): KeyScopeBadge | undefined {
	if (!scopes || scopes.length === 0) return undefined;
	if (scopes.length === 1 && scopes[0] === "*:*:all") return undefined;
	if (scopes.every((scope) => scope.split(":")[2] === "read")) return { label: "Read-only" };
	return { label: "Custom", scopes };
}

/** Whether one stored scope grants the requested resource, id and action. */
function scopeMatches(stored: string, resource: string, id: string, action: string): boolean {
	const parts = stored.split(":");
	if (parts.length !== 3) return false;
	const [storedResource, storedId, storedAction] = parts;
	if (storedResource !== "*" && storedResource !== resource) return false;
	if (storedId !== "*" && storedId !== id) return false;
	return storedAction === "all" || storedAction === action;
}

/** Whether a bundle grants the requested resource, id and action. */
export function scopeAllows(stored: string[], resource: string, id: string, action: string): boolean {
	return stored.some((scope) => scopeMatches(scope, resource, id, action));
}

/**
 * The scopes a signed token (`lim_st_` plus a JWT) carries, read from its payload without checking
 * the signature. For display decisions only: the router and the instance verify and enforce them.
 * Undefined when the token is not a readable signed token, such as an older unsigned one.
 */
export function signedTokenScopes(token: string): string[] | undefined {
	if (!token.startsWith("lim_st_")) return undefined;
	const payload = token.slice("lim_st_".length).split(".")[1];
	if (!payload) return undefined;
	try {
		const claims: unknown = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
		const scopes = (claims as { scopes?: unknown }).scopes;
		return Array.isArray(scopes) && scopes.every((scope) => typeof scope === "string") ? scopes : undefined;
	} catch {
		return undefined;
	}
}

export type OrganizationScopes = {
	role: components["schemas"]["Role"] | undefined;
	/** Whether the caller's role in this organization may do `action` on every `resource`. */
	can: (resource: string, action: string) => boolean;
	/**
	 * The role is known and lacks `action` on `resource`. False while whoami is
	 * still loading, so a page never tells an admin what their role cannot do
	 * on a guess; use it for notices, `can` for enabling controls.
	 */
	denied: (resource: string, action: string) => boolean;
	/** Which switches this deployment has on, so the console offers only what the API accepts. */
	features: components["schemas"]["Features"];
	/** False until whoami answered once; pages must not redirect or hide on a guess. */
	loaded: boolean;
};

const NO_FEATURES: components["schemas"]["Features"] = { apiKeyPermissions: false, apiKeyScopes: false, roles: false };

/**
 * The SWR key of the whoami record. The auth provider seeds it with the
 * response it already fetched; mutate it after joining, creating or changing
 * a seat so the change shows without a reload.
 */
export const WHOAMI_KEY = "/v1/whoami";

/**
 * Refresh the whoami record after a seat changed. A refresh that fails
 * leaves the last good record in place and SWR retries; it must never read
 * as the write failing, which the server already applied.
 */
export function refreshWhoAmI(): Promise<void> {
	return mutate(WHOAMI_KEY).then(
		() => undefined,
		() => undefined,
	);
}

/**
 * Record the seat a just-created organization gives its creator, before the
 * page switches to it, so the switch never waits on the network. The server
 * always seats the creator as admin; the record is refreshed right after and
 * a refresh that fails leaves this seat in place.
 */
export function seatCreatedOrganization(organization: components["schemas"]["Organization"]): Promise<void> {
	const seat: components["schemas"]["Membership"] = { organizationId: organization.tid, role: "admin", permissions: ["*:*:all"], scopes: ["*:*:all"] };
	return mutate(
		WHOAMI_KEY,
		(current: { data?: components["schemas"]["WhoAmI"] } | undefined) =>
			current?.data ? { ...current, data: { ...current.data, memberships: [...(current.data.memberships ?? []), seat] } } : current,
		{ revalidate: true },
	).then(
		() => undefined,
		() => undefined,
	);
}

/** The caller's whoami: seats with what each role may do, and the deployment's switches. */
export function useWhoAmI(): components["schemas"]["WhoAmI"] | "loading" {
	const auth = useAuth();
	const { data } = useSWR(WHOAMI_KEY, async () => {
		const res = await auth.client.GET(WHOAMI_KEY);
		// A failed revalidation must not replace the last good record with
		// nothing; throwing keeps it and lets SWR retry.
		if (!res.data) throw new Error(`whoami answered ${res.response.status}`);
		return res;
	});
	return data?.data ?? "loading";
}

/**
 * The caller's power in the organization being viewed. No membership means
 * nothing is allowed, as on the server.
 */
export function useOrganizationScopes(organization: components["schemas"]["Organization"] | "loading"): OrganizationScopes {
	const whoami = useWhoAmI();
	return useMemo(() => {
		const loaded = whoami !== "loading";
		// TODO(turkenh): delete after the phase 2 deploy. A backend from before
		// memberships were reported answers without them for the minutes a
		// rollout takes; every seat is admin there, so nothing is gated.
		const legacyBackend = loaded && whoami.memberships === undefined;
		const membership =
			organization === "loading" || !loaded ? undefined : whoami.memberships?.find((m) => m.organizationId === organization.tid);
		const can = (resource: string, action: string) =>
			legacyBackend || (membership !== undefined && scopeAllows(membership.permissions ?? membership.scopes, resource, "*", action));
		return {
			role: membership?.role,
			can,
			denied: (resource: string, action: string) => loaded && !can(resource, action),
			features: loaded ? (whoami.features ?? NO_FEATURES) : NO_FEATURES,
			loaded,
		};
	}, [whoami, organization]);
}
